import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { ShipRoute } from '@/types/order'

const FIVE_MINUTES = 5 * 60 * 1000
const GET_PRODUCT_LINE_PRICING = gql`
  query GetProductLinePricing($input: ProductLinePricingInput!) {
    getProductLinePricing(input: $input) {
      priceCents
      productId
    }
  }
`

export interface Pricing {
  priceCents: number
  productId: string
}

interface ProductLinePricingParams extends ShipRoute {
  productIds: string[]
  experimentVariationId?: string
}

interface GetProductLinePricingResponse {
  getProductLinePricing: Pricing[]
}

/**
 * Hook to fetch product pricing based on the ship route
 * @param shipRoute - The ship route to fetch product pricing for
 * @returns Query result containing pricing data, loading, and error
 */

export const useProductLinePricingApi = (
  params: ProductLinePricingParams,
): UseQueryResult<Pricing[], Error> => {
  const client = useGraphQLClient()
  const { productIds, origin, destination, experimentVariationId } = params
  const {
    countryCode: originCountryCode,
    postalCode: originPostalCode,
    state: originState,
  } = origin
  const {
    countryCode: destinationCountryCode,
    postalCode: destinationPostalCode,
    state: destinationState,
  } = destination

  const fetchProductLinePricing = async (): Promise<Pricing[]> => {
    if (!params || !productIds || !origin || !destination || productIds.length === 0) return []

    const data = await client.request<GetProductLinePricingResponse>(GET_PRODUCT_LINE_PRICING, {
      input: { productIds, origin, destination, experimentVariationId },
    })
    return data.getProductLinePricing
  }

  return useQuery<Pricing[], Error>({
    queryKey: [
      'productLinePricing',
      originCountryCode,
      destinationCountryCode,
      originPostalCode,
      originState,
      destinationPostalCode,
      destinationState,
      productIds,
      experimentVariationId,
    ],
    queryFn: fetchProductLinePricing,
    retry: (failureCount, error) => {
      return failureCount < 3 && error instanceof Error && error.message.includes('network')
    },
    staleTime: FIVE_MINUTES,
    enabled: !!(
      productIds.length > 0 &&
      originCountryCode &&
      destinationCountryCode &&
      (originPostalCode || originState) &&
      (destinationPostalCode || destinationState)
    ),
  })
}
